import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from '../contexts/AuthContext';

const useAuthentication = () => {
    const {
        currentUser,
        updateUser,
        clearUserData,
    } = useAuthContext();

    const [requires2FA, setRequires2FA] = useState(false);

    const navigate = useNavigate();

    const isAuthenticated = useMemo(() => !!currentUser?.id, [currentUser]);

    const shouldConfigure2FA = useMemo(() => currentUser?.is_2fa_required && !currentUser?.is_2fa_configured, [currentUser]);
    const shouldChangePassword = useMemo(() => currentUser?.do_force_password_change, [currentUser]);
    const shouldAgreeToTOC = useMemo(() => !currentUser?.has_company_agreed_toc, [currentUser]);

    const authenticate = async ({ email, password, rememberMe }) => {
        try {
            const response = await fetch('/v0/authenticate', {
                credentials: 'same-origin',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: email,
                    password: password,
                    persistent_cookie: rememberMe,
                }),
            });

            if (!response.ok) throw response;

            const json = await response.json();

            updateUser(json.result);
        } catch (error) {
            const errorText = await error.text();

            if (errorText?.trim()?.match(/EREQUIRE2FA/)) {
                setRequires2FA(true);
                return;
            }

            console.error('Error during authentication:', error);
            throw error;
        }
    };

    const authenticate2FA = async ({ email, password, rememberMe, verificationCode }) => {
        try {
            const response = await fetch('/v0/authenticate2fa', {
                credentials: 'same-origin',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: email,
                    password: password,
                    persistent_cookie: rememberMe,
                    verification_code: verificationCode,
                }),
            });

            if (!response.ok) throw response;

            const json = await response.json();

            updateUser(json.result);
        } catch (error) {
            console.error('Error during 2FA authentication:', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await fetch('/v0/logout', {
                credentials: 'same-origin',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            clearUserData();

            navigate('/login');
        } catch (error) {
            console.error('Error during logout:', error);
            throw error;
        }
    };

    return {
        currentUser,
        isAuthenticated,
        shouldConfigure2FA,
        shouldChangePassword,
        shouldAgreeToTOC,
        updateUser,
        requires2FA,
        authenticate,
        authenticate2FA,
        logout,
    };
};

export default useAuthentication;
